import React from 'react';
import { Users, CalendarCheck, ShoppingBag, Network, Sparkles, LayoutGrid } from 'lucide-react';

const cards = [
  { key: 'follow_ups_today', label: 'Seguimientos hoy', icon: CalendarCheck },
  { key: 'prospects_total', label: 'Prospectos en CRM', icon: Users },
  { key: 'month_sales_total', label: 'Ventas del mes', icon: ShoppingBag },
  { key: 'team_size', label: 'Equipo', icon: Network },
];

export const Card = ({ children, className = '', testId }) => (
  <section className={`rounded-[26px] border border-[#0B3F8C]/10 bg-white p-6 shadow-[0_18px_50px_rgba(11,63,140,0.08)] ${className}`} data-testid={testId}>{children}</section>
);

export const SectionTitle = ({ kicker, title, subtitle, action, brandColor = '#E2001A' }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      {kicker && <p className="text-[11px] font-bold uppercase tracking-[0.3em]" style={{ color: brandColor }}>{kicker}</p>}
      <h2 className="mt-1 text-2xl font-black tracking-tight text-[#062A5E]">{title}</h2>
      {subtitle && <p className="mt-1 max-w-2xl text-sm text-slate-500">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const inputCls = 'w-full rounded-xl border border-slate-200 bg-[#f8fafc] px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-[#0B3F8C] focus:bg-white';
export const btnPrimary = 'inline-flex items-center justify-center gap-2 rounded-full bg-[#0B3F8C] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#062A5E] disabled:opacity-50';
export const btnAccent = 'inline-flex items-center justify-center gap-2 rounded-full bg-[#E2001A] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#b80015] disabled:opacity-50';
export const btnGhost = 'inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-[#0B3F8C] hover:text-[#0B3F8C] disabled:opacity-50';

// Line selector for multi-line workspaces (e.g. Milagros: Oriflame + FisioMedic)
const LineSelector = ({ lines, activeLine, onSwitch }) => {
  if (!lines || lines.length < 2) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-[22px] border border-white/20 bg-white/10 px-5 py-3 backdrop-blur" data-testid="mlm-line-selector">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.3em] text-blue-100/80">
        <LayoutGrid className="h-3.5 w-3.5" /> Línea de negocio
      </div>
      <div className="flex flex-wrap gap-2">
        {lines.map((l) => {
          const isActive = activeLine === l.cid;
          return (
            <button
              key={l.cid}
              type="button"
              onClick={() => !isActive && onSwitch(l.cid)}
              data-testid={`mlm-line-tab-${l.business_line}`}
              style={isActive ? { backgroundColor: l.colors.primary, boxShadow: `0 4px 16px ${l.colors.primary}50` } : {}}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-all duration-200 ${isActive ? 'text-white scale-105' : 'bg-white/15 text-white ring-1 ring-white/30 hover:bg-white/25'}`}
            >
              {l.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const MlmShell = ({ workspace, activeSection, onSectionChange, activeLine, onSwitchLine }) => {
  const { brand, user, metrics, sections, profile, multi_line, available_lines } = workspace;

  // Brand colors — use brand.colors if available (MVL lines), else defaults
  const primaryColor = brand?.colors?.primary || '#0B3F8C';
  const primaryDark = brand?.colors?.primary_dark || '#062A5E';
  const accentColor = brand?.colors?.accent || '#E2001A';
  const softBg = brand?.colors?.soft || '#f3f6fb';

  const shopUrl = profile?.shop_url;
  const registrationUrl = profile?.registration_url;
  const showShopLinks = shopUrl || registrationUrl;

  return (
    <section className="overflow-hidden rounded-[32px] border border-white/70 text-white shadow-[0_30px_90px_rgba(6,42,94,0.35)]"
      style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryDark} 100%)` }}
      data-testid="mlm-shell">
      <div className="relative p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full blur-3xl" style={{ backgroundColor: `${accentColor}40` }} />
        <div className="pointer-events-none absolute -left-10 bottom-0 h-56 w-56 rounded-full bg-white/10 blur-3xl" />

        {/* Multi-line selector */}
        {multi_line && available_lines?.length > 1 && (
          <LineSelector lines={available_lines} activeLine={activeLine} onSwitch={onSwitchLine} />
        )}

        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.35em] text-white/60" data-testid="mlm-brand-name">{brand.name}</p>
            <h1 className="mt-2 text-4xl font-black tracking-tight sm:text-5xl" data-testid="mlm-heading">
              Hola, {(user.nombre || '').split(' ')[0]} 👋
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-white/80 sm:text-base" data-testid="mlm-tagline">{brand.tagline}</p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              {showShopLinks && shopUrl && (
                <a href={shopUrl} target="_blank" rel="noreferrer"
                  className="rounded-full bg-white/10 px-3 py-1.5 font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20"
                  data-testid="mlm-shop-link">🛒 Mi tienda</a>
              )}
              {showShopLinks && registrationUrl && (
                <a href={registrationUrl} target="_blank" rel="noreferrer"
                  className="rounded-full bg-white/10 px-3 py-1.5 font-semibold text-white ring-1 ring-white/20 transition hover:bg-white/20"
                  data-testid="mlm-registration-link">🤝 Invitar socios</a>
              )}
              <button type="button" onClick={() => onSectionChange('aynara')}
                className="rounded-full px-3 py-1.5 font-bold text-white transition"
                style={{ backgroundColor: accentColor }}
                data-testid="mlm-open-aynara">
                <Sparkles className="mr-1 inline h-3.5 w-3.5" />Hablar con Aynara
              </button>
            </div>
          </div>
          <div className="rounded-[22px] bg-white/10 px-5 py-4 text-sm ring-1 ring-white/15 backdrop-blur" data-testid="mlm-user-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/60">Sesión activa</p>
            <p className="mt-1 text-lg font-bold" data-testid="mlm-user-name">{user.nombre}</p>
            <p className="text-white/70" data-testid="mlm-user-role">{user.role_label}</p>
            <p className="mt-2 text-[11px] text-white/50">ID: {workspace.client_identity?.client_id}</p>
          </div>
        </div>

        <div className="relative mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {cards.map(({ key, label, icon: Icon }) => (
            <article key={key} className="rounded-[22px] bg-white px-5 py-4 text-slate-900" data-testid={`mlm-metric-${key}`}>
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</p>
                <Icon className="h-4 w-4" style={{ color: primaryColor }} />
              </div>
              <p className="mt-2 text-3xl font-black" style={{ color: primaryColor }}>{metrics[key] ?? 0}</p>
            </article>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 bg-white px-6 py-4 sm:px-8" data-testid="mlm-section-switcher">
        {sections.map((s) => (
          <button key={s.id} type="button" onClick={() => onSectionChange(s.id)}
            className="rounded-full px-4 py-2 text-sm font-semibold transition"
            style={activeSection === s.id ? { backgroundColor: primaryColor, color: 'white', boxShadow: `0 4px 14px ${primaryColor}50` } : { backgroundColor: '#f1f5fb', color: '#475569' }}
            data-testid={`mlm-section-${s.id}-button`}>{s.label}</button>
        ))}
      </div>
    </section>
  );
};
